import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CircleCheck, CircleAlert, Loader2 } from 'lucide-react';
import PageHeader from '../../components/site/PageHeader';
import SEO from '../../components/SEO';
import accountService from '../../services/accountService';

/**
 * Opened from the verification email: /verify-email?uid=...&token=...
 * Works signed in or out; the token alone proves the link came from the email.
 */
const VerifyEmailPage = () => {
  const [params] = useSearchParams();
  const userId = params.get('uid') || '';
  const token = params.get('token') || '';
  const [state, setState] = useState(userId && token ? { status: 'verifying' } : { status: 'error', message: 'This verification link is incomplete. Open it again from the email, or request a new one from your account.' });
  const started = useRef(false);

  useEffect(() => {
    if (!userId || !token || started.current) return;
    started.current = true; // the token is checked once, even under StrictMode's double effects
    accountService.verifyEmail({ userId, token }).then(result => {
      setState(result.success ? { status: 'done', message: result.message } : { status: 'error', message: result.error });
    });
  }, [userId, token]);

  return (
    <main className="pk-page">
      <SEO title="Verify email" noindex />
      <div className="pk-container" style={{ maxWidth: 640 }}>
        <PageHeader title="Verify your email" crumbs={[{ label: 'Home', to: '/' }, { label: 'Verify email' }]} />
        <section className="pk-card" aria-live="polite">
          {state.status === 'verifying' && (
            <div className="pk-alert" data-tone="info">
              <Loader2 aria-hidden="true" />
              <span>Checking your link…</span>
            </div>
          )}
          {state.status === 'done' && (
            <div className="pk-stack">
              <div className="pk-alert" data-tone="success" role="status">
                <CircleCheck aria-hidden="true" />
                <span>{state.message || 'Thanks, your email address is verified.'}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <Link className="pk-btn pk-btn--primary pk-btn--sm" to="/profile">
                  Go to my account
                </Link>
                <Link className="pk-btn pk-btn--secondary pk-btn--sm" to="/products">
                  Browse projects
                </Link>
              </div>
            </div>
          )}
          {state.status === 'error' && (
            <div className="pk-stack">
              <div className="pk-alert" data-tone="danger" role="alert">
                <CircleAlert aria-hidden="true" />
                <span>{state.message}</span>
              </div>
              <div>
                <Link className="pk-btn pk-btn--secondary pk-btn--sm" to="/profile?tab=account">
                  Request a new link
                </Link>
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default VerifyEmailPage;
